import React, { useEffect, useState } from 'react';
import { Box, Grid, TextField, MenuItem } from '@mui/material';
import { apiFetch } from '../../utils/api';

// 50 US states (2-letter codes)
const STATES = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

// Membership options
const MEMBERSHIP_OPTIONS = [
    { value: 'single', label: 'Single Membership — $25' },
    { value: 'couple', label: 'Couple Membership — $45' },
    { value: 'family', label: 'Family Membership — $60' },
];

export default function MembershipForm({
                                           formId = 'membership-form',
                                           onSuccess,
                                           onError,
                                           disabled = false,
                                           paymentMethod = '',     // 'paypal' | 'check'
                                           paypalOrderId = '',
                                           membershipType,         // controlled value from parent (optional)
                                           onMembershipTypeChange, // callback to bubble selection to parent
                                       }) {
    // Controlled or local state for membership type
    const [type, setType] = useState(membershipType || 'single');
    useEffect(() => {
        setType(membershipType || 'single');
    }, [membershipType]);

    const [firstName, setFirstName] = useState('');
    const [lastName,  setLastName]  = useState('');
    const [email,     setEmail]     = useState('');
    const [phone,     setPhone]     = useState('');
    const [street,    setStreet]    = useState('');
    const [city,      setCity]      = useState('');
    const [state,     setState]     = useState('AL');
    const [zip,       setZip]       = useState('');

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                first_name:      firstName.trim(),
                last_name:       lastName.trim(),
                email:           email.trim(),
                phone:           phone.trim(),
                street_address:  street.trim(),
                city:            city.trim(),
                state:           state.trim(),
                zip_code:        zip.trim(),
                membership_type: type,
                payment_method:  paymentMethod || 'check',
                paypal_order_id: paypalOrderId || '',
            };

            const resp = await apiFetch('/api/members', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (resp.ok) {
                onSuccess && onSuccess();
            } else {
                const data = await resp.json().catch(() => ({}));
                const msg = data?.error ? (Array.isArray(data.error) ? data.error.join(', ') : data.error) : 'Request failed.';
                onError && onError(msg);
            }
        } catch (err) {
            onError && onError('Network error. Please try again.');
        }
    };

    return (
        <Box component="form" id={formId} onSubmit={onSubmit} noValidate autoComplete="on">
            <Grid container spacing={2}>
                {/* Membership Type (ABOVE contact details) */}
                <Grid item xs={12}>
                    <TextField
                        name="membership_type"
                        label="Membership Type"
                        select
                        required
                        fullWidth
                        value={type}
                        onChange={(e) => {
                            setType(e.target.value);
                            onMembershipTypeChange && onMembershipTypeChange(e.target.value);
                        }}
                        disabled={disabled}
                    >
                        {MEMBERSHIP_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        name="first_name"
                        label="First Name"
                        required
                        fullWidth
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={disabled}
                        autoComplete="given-name"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        name="last_name"
                        label="Last Name"
                        required
                        fullWidth
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={disabled}
                        autoComplete="family-name"
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        name="email"
                        label="Email"
                        type="email"
                        required
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={disabled}
                        autoComplete="email"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        name="phone"
                        label="Phone"
                        required
                        fullWidth
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        // Simpler, cross-browser safe pattern (digits, spaces, parentheses, dash, plus, dot)
                        inputProps={{ pattern: '^[0-9()+\\- .]+$' }}
                        disabled={disabled}
                        autoComplete="tel"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        name="street_address"
                        label="Street Address"
                        required
                        fullWidth
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        disabled={disabled}
                        autoComplete="address-line1"
                    />
                </Grid>

                {/* City / State / ZIP on the same row (city smaller) */}
                <Grid item xs={12} sm={5}>
                    <TextField
                        name="city"
                        label="City"
                        required
                        fullWidth
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={disabled}
                        autoComplete="address-level2"
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        name="state"
                        label="State"
                        select
                        required
                        fullWidth
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        disabled={disabled}
                        autoComplete="address-level1"
                    >
                        {STATES.map((s) => (
                            <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <TextField
                        name="zip_code"
                        label="ZIP Code"
                        required
                        fullWidth
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                        inputProps={{ inputMode: 'numeric', pattern: '\\d{5}(-\\d{4})?' }}
                        disabled={disabled}
                        autoComplete="postal-code"
                    />
                </Grid>

                {/* Hidden fields (optional helpers) */}
                <input type="hidden" name="payment_method" value={paymentMethod || 'check'} />
                <input type="hidden" name="paypal_order_id" value={paypalOrderId || ''} />
            </Grid>

            {/* NOTE: The submit button is hidden by the parent via CSS; parent calls requestSubmit() */}
            <button type="submit" style={{ display: 'none' }}>Submit</button>
        </Box>
    );
}
